import { alpha, Theme } from '@mui/material/styles';
import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useHackathon } from '../../hooks/useHackathon';
import { Loading } from '../../components/Loading';
import { enableVotingPeriod, endVotingPeriod, getRawVotes } from '../../services/hackathon.service';
import GavelIcon from '@mui/icons-material/Gavel';
import PeopleIcon from '@mui/icons-material/People';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { IdeaVoting } from '../../components/Voting/IdeaVoting';
import { useAuth } from '../../services/useAuth';
import { PageContainer } from '../../components/PageContainer';

import HomeIcon from '@mui/icons-material/Home';
import CodeIcon from '@mui/icons-material/Code';
import { JudgeVoteBadge } from '../../components/Voting/JudgeVoteBadge';
import { IdeaSelect } from '../../components/Idea/IdeaSelect';
import Alert from '@mui/material/Alert/Alert';
import Stack from '@mui/material/Stack/Stack';
import Breadcrumbs from '@mui/material/Breadcrumbs/Breadcrumbs';
import Button from '@mui/material/Button/Button';
import Typography from '@mui/material/Typography/Typography';
import Box from '@mui/material/Box/Box';
import FormControlLabel from '@mui/material/FormControlLabel/FormControlLabel';
import Switch from '@mui/material/Switch/Switch';
import Accordion from '@mui/material/Accordion/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary/AccordionSummary';
import { CategorySortMenu } from '../../components/Voting/CategorySortMenu';
import { AppBreadcrumbs, PageType } from '../../components/Breadcrumbs/Buttons';
import { IdeaPayload, IdeaScorecard } from '../../services/idea.service';
import { aggregateVotes, getJudgingProgress, isSavedVote, sendLastCall, JudgeProgress, ParticipantProgress, votesAtom } from '../../services/vote.service';
import { useAtomValue } from 'jotai';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Chip from '@mui/material/Chip/Chip';
import { VoteData, VotePayload } from '../../models/vote.model';
import Markdown from 'react-markdown';
import { useSessions } from '../../hooks/useSessions';
import { dueTeams, isEntry, participantsScore } from '../../utils/sessions';
import { resultsAnnounced } from '../../utils/hackathonStatus';
import { AnnounceWinners, judgeTotal } from '../../components/Voting/AnnounceWinners';
import { HelpLink } from '../../components/HelpLink';
import { useLiveBoard } from '../../hooks/useLiveSessions';
import { useHackathonSync } from '../../hooks/useHackathonSync';

const participantTotal = (categories: { [category: string]: IdeaScorecard }) =>
    Object.values(categories).reduce((total, scores) => total + (scores?.participantVoteAverage || 0), 0);

/**
 * The purpose of the Judge Page is for judges to view their votes and optionally look at how other judges have voted.
 * 
 * Each column represents a different category that a project is judged on
 * Each row represents a different project
 * 
 * Each cell will contain the score that the judge gave to the project in that category, 
 * as well as a comparison to the average score given by all judges, once there are multiple judge scores.
 * 
 * 
 */

// score swatches follow the brand so judges see the same colors as the rest of the hackathon
const judgeScoreBg = (theme: Theme) => alpha(theme.palette.primary.main, 0.16);
const participantScoreBg = (theme: Theme) => alpha(theme.palette.secondary.main, 0.18);

export const JudgePage = () => {
    const { id } = useParams();

    const { hackathon, loading, error, reload } = useHackathon(id);
    const { sessions } = useSessions(id);
    // the live board: lineups, leave-outs and who has presented, a few seconds old; the rest follows its version
    const board = useLiveBoard(id);
    useHackathonSync(id, board.version, hackathon?.participants.length || 0);

    const { user } = useAuth();

    const summary = useMemo(() => {
        if (!hackathon) return null;

        return {
            results: hackathon.results,
            categories: hackathon.categories,
        }
    }, [hackathon])

    const [sortCategory, setSortCategory] = React.useState<string | null>(null);
    const [sortType, setSortType] = React.useState<string>('judge');

    const [showJudgeVotes, setShowJudgeVotes] = React.useState<string[]>([]);

    const [highlightedIdea, setHighlightedIdea] = React.useState<string | null>(null);

    const ratedProjects = useMemo(() => {
        if (!hackathon || !summary || !summary.results) return null;

        let projectIds: Set<string> = new Set();
        const results = summary.results;
        // every idea has a results row, scored or not; only real scores keep a team on the page outside a lineup
        results.forEach(result => {
            result.ideas.forEach(idea => {
                if (idea.participantVoteAverage > 0 || idea.judgeVotes.some(j => j.score > 0)) {
                    projectIds.add(idea.ideaId);
                }
            })
        })
        // the rooms' lineups in presenting order (the hosts pick them; teams left out everywhere aren't judged),
        // then any other team that has scores anyway, e.g. left out after voting started
        const lineups = board.sessions.length ? board.sessions.map(s => s.ideas) : sessions.map(s => s.ideas);
        const order = Array.from(new Set([...lineups.flat(), ...hackathon.ideas.map(i => i._id).filter(id => projectIds.has(id))]));
        const projects = order.map(_id => hackathon.ideas.find(i => i._id === _id)).filter(i => !!i && isEntry(i)) as IdeaPayload[];

        const projectResults = projects.map(proj => {
            const projectVotes: { [key: string]: IdeaScorecard } = {}
            results.filter(r => r.ideas.some(idea => idea.ideaId === proj._id)).forEach(r => {
                const scores = r.ideas.find(idea => idea.ideaId === proj._id);
                if (scores) {
                    projectVotes[r.category] = scores;
                }
            })
            return {
                ...proj,
                categories: projectVotes
            }
        })

        return projectResults.sort((a, b) => {
            if (sortCategory) {
                if (sortType === 'participant') {
                    return (b.categories[sortCategory]?.participantVoteAverage || 0) - (a.categories[sortCategory]?.participantVoteAverage || 0)
                } else {
                    return (b.categories[sortCategory]?.judgeVoteAverage || 0) - (a.categories[sortCategory]?.judgeVoteAverage || 0)
                }
            }
            return 0;
        });
    }, [summary, hackathon, sortCategory, sessions, board.sessions])

    // rank by judges' total (what Announce winners suggests); ties share a rank, unscored teams get none
    const judgeRanks = useMemo(() => {
        const totals = (ratedProjects || []).map(p => judgeTotal(p)).filter(t => t > 0);
        const ranks: { [ideaId: string]: number } = {};
        (ratedProjects || []).forEach(p => {
            const total = judgeTotal(p);
            if (total > 0) {
                ranks[p._id] = 1 + totals.filter(t => t > total).length;
            }
        });
        return ranks;
    }, [ratedProjects])

    const ratedProjectElementRefs = useMemo(() => {
        return ratedProjects?.map(proj => React.createRef<HTMLTableRowElement>()) ?? [];
    }, [ratedProjects])

    const isJudgeOrOrganizer = useMemo(() => {
        return user && hackathon && (hackathon.judges.some(j => j.email === user.email) || hackathon.organizers.some(o => o.email === user.email));
    }, [hackathon, user])

    const isJudge = useMemo(() => {
        return user && hackathon && hackathon.judges.some(j => j.email === user.email);
    }, [hackathon, user])

    const isOrganizer = useMemo(() => {
        return user && hackathon && hackathon.organizers.some(o => o.email === user.email);
    }, [hackathon, user])

    const [expandedProjects, setExpandedProjects] = React.useState<string[]>([]);
    const handleExpand = (ideaId: string) => {
        if (expandedProjects.includes(ideaId)) {
            setExpandedProjects(expandedProjects.filter(id => id !== ideaId))
        } else {
            setExpandedProjects([...expandedProjects, ideaId]);
        }
    }

    const judgeOrder = useMemo(() => {
        return hackathon?.judges.map(j => j._id) ?? [];
    }, [hackathon])

    // the signed-in user's saved votes (each team's score card loads its own), so a judge sees where they are without opening every panel
    const votes = useAtomValue(votesAtom);
    // idea id -> the scores you gave it
    const scoredIdeas = useMemo(() => {
        const scored = new Map<string, number[]>();
        Object.values(votes).forEach(v => {
            if (isSavedVote(v)) {
                scored.set(v.ideaId, v.scores || []);
            }
        });
        return scored;
    }, [votes])

    // organizers: who has scored how many teams, refreshed while voting is open, so closing voting needs no roll call
    const [judgeProgress, setJudgeProgress] = React.useState<JudgeProgress[]>([]);
    const [participantProgress, setParticipantProgress] = React.useState<ParticipantProgress | null>(null);
    // after End Voting: who hadn't finished, with a way back (reopening is the undo)
    const [closedEarly, setClosedEarly] = React.useState<string | null>(null);
    // what the last "Last call" did, shown next to the buttons
    const [lastCall, setLastCall] = React.useState<string | null>(null);
    const showJudgeProgress = !!hackathon && !!user && hackathon.organizers.some(o => o.email === user.email) && !!hackathon.votingPeriodOn;
    React.useEffect(() => {
        if (!showJudgeProgress || !hackathon) {
            return;
        }
        let timer = 0;
        let stopped = false;
        const load = () => {
            const next = () => { if (!stopped) timer = window.setTimeout(load, document.hidden ? 30000 : 10000); };
            getJudgingProgress(hackathon._id).then(progress => {
                setJudgeProgress(progress.judges);
                setParticipantProgress(progress.participants);
            }, err => console.error(err)).finally(next);
        };
        load();
        return () => { stopped = true; window.clearTimeout(timer); };
    }, [showJudgeProgress, hackathon?._id])

    const [openVotes, setOpenVotes] = React.useState<string[]>([]);
    const toggleVote = (ideaId: string) => {
        setOpenVotes(openVotes.includes(ideaId) ? openVotes.filter(i => i !== ideaId) : [...openVotes, ideaId]);
    }


    // only before the first load: a reload (after opening voting, announcing...) must not unmount the page under the user
    if (loading && !hackathon) {
        return <Loading />
    }

    if (!hackathon || !summary || !summary.results) {
        return null;
    }

    if (!isJudgeOrOrganizer) {
        return <Alert severity='error'>You are not a judge or organizer for this hackathon</Alert>
    }

    if (!ratedProjects || ratedProjects.length === 0) {
        return <Alert severity='info'>No projects have been rated yet</Alert>
    }

    // the most a total can be: 10 per category (participants don't score judges-only ones)
    const judgeMax = 10 * hackathon.categories.length;
    const participantMax = 10 * hackathon.categories.filter((_, i) => !hackathon.categoryMeta[i]?.judgeOnly).length;

    // what this judge is expected to score: in the rooms they judge, the teams that have presented (or the whole lineup
    // before anyone has), so a team that never presents doesn't hold up "all done"
    const myRooms = board.sessions.filter(live => sessions.find(s => s._id === live._id)?.judges.some(j => !!user && j.email === user.email));
    const due = new Set((myRooms.length ? myRooms : board.sessions).flatMap(live => dueTeams(live)));
    const isDue = (ideaId: string) => board.sessions.length === 0 || due.has(ideaId);
    const dueProjects = ratedProjects.filter(p => isDue(p._id));
    const scoredCount = dueProjects.filter(p => scoredIdeas.has(p._id)).length;
    const nextToScore = dueProjects.find(p => !scoredIdeas.has(p._id));
    // teams in the lineup that haven't presented yet (once presentations have started)
    const toCome = ratedProjects.length - dueProjects.length;
    const room = myRooms[0] || board.sessions[0];
    const scoreNext = () => {
        if (!nextToScore) return;
        const index = ratedProjects.findIndex(p => p._id === nextToScore._id);
        if (!openVotes.includes(nextToScore._id)) {
            setOpenVotes([...openVotes, nextToScore._id]);
        }
        setHighlightedIdea(nextToScore._id);
        ratedProjectElementRefs[index]?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }


    return (
        <PageContainer>
            <Stack direction="column" spacing={2}>
                <AppBreadcrumbs crumbs={[{
                    type: PageType.Home,
                    link: '/',
                    name: 'Home'
                }, {
                    type: PageType.Hackathon,
                    link: `/h/${id}`,
                    name: hackathon?.name || ''
                }, {
                    type: PageType.Judge,
                    name: 'Judging'
                }]} />

                <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap spacing={1}>
                    <h1>Judging - {hackathon.name}</h1>
                    <HelpLink guide={isJudge ? 'vote' : 'scoring'}>{isJudge ? 'How judging works' : 'How scores are counted'}</HelpLink>
                </Stack>
                <Typography>
                    {
                        resultsAnnounced(hackathon) ? <>The results are announced. The scores below are final.</>
                            : hackathon.votingPeriodOn ? <>Voting is open: score each team under <b>My vote</b>. Everyone's scores update here as they come in.</>
                                : <>Score each team under <b>My vote</b> once voting opens. Everyone's scores update here as they come in.</>
                    }
                </Typography>
                {
                    // where the judge is, and the next team to score, in one place
                    // the done state keeps a button, so it reads as a step finished rather than the Score next button vanishing
                    isJudge && hackathon.votingPeriodOn && <Alert severity={nextToScore ? 'info' : 'success'} role="status"
                        action={nextToScore
                            ? <Button color="inherit" size="small" onClick={scoreNext}>Score next: {nextToScore.title}</Button>
                            : toCome > 0 && room
                                ? <Button color="inherit" size="small" component={Link} to={`/h/${hackathon.slug}/s/${room.slug}`}>Watch the room</Button>
                                : <Button color="inherit" size="small" component={Link} to={`/h/${hackathon.slug}`}>Back to {hackathon.name}</Button>}>
                        {nextToScore
                            ? <>You've scored <b>{scoredCount} of {dueProjects.length}</b> {dueProjects.length === 1 ? 'team' : 'teams'}{toCome > 0 ? ' presented so far' : ''}.</>
                            : toCome > 0
                                ? <>Up to date: you've scored {dueProjects.length === 1 ? 'the' : 'all'} <b>{dueProjects.length}</b> {dueProjects.length === 1 ? 'team' : 'teams'} presented so far. <b>{toCome}</b> still to present.</>
                                : <><b>All done:</b> you've scored all <b>{dueProjects.length}</b> {dueProjects.length === 1 ? 'team' : 'teams'}. You can still change a vote until voting closes.</>}
                    </Alert>
                }
                {
                    !isJudge && <Alert severity='info'>As an organizer, you may use this page but please note your votes do not influence judges</Alert>
                }
                {error && <Alert severity='error'>{error.message}</Alert>}
                {/* how to read the table, folded: on a phone it filled the first screen (Hermione, runs 8-32) */}
                <Accordion disableGutters elevation={0} defaultExpanded={false}
                    sx={{ backgroundColor: 'var(--surface-muted)', borderRadius: '10px', '&::before': { display: 'none' } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography sx={{ fontWeight: 600 }}>How to read this table</Typography>
                    </AccordionSummary>
                    <Box sx={{ px: 2, pb: 2 }}>
                    <Stack direction="column" spacing={1}>
                        {/* judges ask for the scale before voting opens, and look here for it */}
                        <Typography>
                            <b>Scale:</b> each category is scored from <b>1</b> (weak) to <b>10</b> (excellent), or Abstain.
                            <b> Total</b> adds up a team's category averages, and teams are ranked by the judges' total.
                            <b> Show Judge Votes</b> under a team shows each judge's score.
                        </Typography>
                        <Stack direction="row" spacing={1}>
                            <Box sx={{ width: 48, height: 24, backgroundColor: judgeScoreBg, borderRadius: '6px' }}></Box>
                            <Typography>Average score from judges (a "-" means no scores yet)</Typography>
                        </Stack>
                        {
                            hackathon.participantScores && <Stack direction="row" spacing={1}>
                                <Box sx={{ width: 48, height: 24, backgroundColor: participantScoreBg, borderRadius: '6px' }}></Box>
                                <Typography>Average score from participants</Typography>
                            </Stack>
                        }
                    </Stack>

                    </Box>
                </Accordion>
                {
                    isOrganizer && (
                        <Stack spacing={2} direction="row">
                            {
                                hackathon.votingPeriodOn && <Button variant="contained" onClick={async () => {
                                    // fresh counts, not the last poll: a team may have just presented or been left out
                                    const progress = await getJudgingProgress(hackathon._id).catch(() => ({ judges: judgeProgress, participants: participantProgress }));
                                    const judgesLeft = progress.judges.filter(j => j.scored < j.total).map(j => j.username);
                                    // participants needn't score every team; only those who scored nobody are worth a mention
                                    const participantsIdle = progress.participants ? progress.participants.total - progress.participants.done - progress.participants.started : 0;
                                    const notes = [
                                        judgesLeft.length > 0 && `${judgesLeft.join(', ')} hadn't scored every team.`,
                                        participantsIdle > 0 && `${participantsIdle} ${participantsIdle === 1 ? "participant hasn't" : "participants haven't"} scored any team.`
                                    ].filter(Boolean).join(' ');
                                    endVotingPeriod(hackathon._id).then(() => {
                                        setClosedEarly(notes || null);
                                        reload();
                                    });
                                }}>End Voting</Button>
                            }
                            {
                                // before closing: tell the people who still have teams to score, by name of the teams, in their bell
                                hackathon.votingPeriodOn && <Button variant="outlined" onClick={() => {
                                    setLastCall(null);
                                    sendLastCall(hackathon._id).then(n => setLastCall(n > 0
                                        ? `Last call sent to ${n} ${n === 1 ? 'person' : 'people'} with teams left to score.`
                                        : 'Everyone has scored every team they can. Nobody needed a last call.'),
                                        err => setLastCall(err.response?.data?.message || 'Could not send the last call'));
                                }}>Last call</Button>
                            }
                            {
                                !hackathon.votingPeriodOn && (
                                    <Button variant="outlined" onClick={() => {
                                        setClosedEarly(null);
                                        enableVotingPeriod(hackathon._id).then(() => reload());
                                    }}>Open Voting</Button>
                                )
                            }
                            <Button variant="outlined" onClick={() => {
                                aggregateVotes(hackathon._id).then(() => reload());
                            }}>Refresh Results</Button>
                            <Button onClick={() => {
                                getRawVotes(hackathon.slug).then((data: Array<VoteData>) => {
                                    // columns are userId, isJudge, ideaId, ideaTitle, one score per category
                                    const header = ['userId', 'isJudge', 'ideaId', 'ideaTitle', 'session', ...hackathon.categories];
                                    const _data = data.map(row => {
                                        return [row.userId, row.isJudge, row.ideaId, row.ideaTitle, row.sessionName || '', ...row.scores.map(s => s.toString() === '0' ? '' : s.toString())];
                                    })
                                    // quote every field so titles containing commas or quotes stay in their column
                                    const quote = (value: unknown) => `"${String(value ?? '').replace(/"/g, '""')}"`;
                                    const csv = [header, ..._data].map(row => row.map(quote).join(',')).join('\n');
                                    const blob = new Blob([csv], { type: 'text/csv' });
                                    const url = URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = `${hackathon.slug}_${new Date().toISOString().substring(0, 10)}_votes.csv`;
                                    a.click();
                                })
                            }}>Download Raw Votes CSV</Button>
                        </Stack>
                    )
                }
                {
                    // organizers set this when creating the hackathon and forget it by the time voting opens
                    isOrganizer && (
                        <Typography variant="body2" color="text.secondary">
                            {
                                participantsScore(hackathon)
                                    ? <>Participants score too (anonymously, never their own team); their averages show next to the judges'.</>
                                    : hackathon.participantScores
                                    ? <>Only the judges score: every category is <b>Judges Only</b>, so participants have nothing to vote on and aren't asked to.</>
                                    : <>Only the judges score: <b>Participant scores</b> is off for this hackathon, so participants have nothing to vote on.
                                        You can turn it on in <Link to={`/h/${hackathon.slug}/edit`}>Edit hackathon</Link> while voting is closed.</>
                            }
                            {' '}Every idea in the presenting order is judged, with or without a team; leave ideas out from the run of show.
                        </Typography>
                    )
                }
                {
                    showJudgeProgress && judgeProgress.length > 0 && <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap role="status">
                        <Typography variant="body2" color="text.secondary">Scored so far:</Typography>
                        {participantProgress && participantProgress.total > 0 && <Chip size="small"
                            variant={participantProgress.done >= participantProgress.total ? 'filled' : 'outlined'}
                            color={participantProgress.done >= participantProgress.total ? 'success' : 'default'}
                            label={`Participants: ${participantProgress.done} of ${participantProgress.total} done${participantProgress.started ? `, ${participantProgress.started} part way` : ''}`} />}
                        {judgeProgress.map(j => {
                            const done = j.total > 0 && j.scored >= j.total;
                            return <Chip key={j._id} size="small" variant={done ? 'filled' : 'outlined'} color={done ? 'success' : 'default'}
                                icon={done ? <CheckCircleIcon /> : undefined}
                                label={`${j.username}: ${j.scored} of ${j.total}`} />;
                        })}
                    </Stack>
                }
                {
                    isOrganizer && lastCall && hackathon.votingPeriodOn && <Alert severity="info" role="status" onClose={() => setLastCall(null)}>{lastCall}</Alert>
                }
                {
                    isOrganizer && closedEarly && !hackathon.votingPeriodOn && <Alert severity="warning" role="status"
                        action={<Button color="inherit" size="small" onClick={() => {
                            setClosedEarly(null);
                            enableVotingPeriod(hackathon._id).then(() => reload());
                        }}>Reopen voting</Button>}>
                        Voting is closed. {closedEarly}
                    </Alert>
                }
                {
                    isOrganizer && <AnnounceWinners hackathon={hackathon} projects={ratedProjects} onAnnounced={() => reload()} />
                }

                <Box sx={{ overflowX: 'auto' }}>
                <table style={{ borderSpacing: 0, width: '100%' }}>
                    <thead style={{
                        backgroundColor: 'white',
                        boxShadow: '0 1px 0 var(--border-color)',
                        position: 'sticky',
                        top: -1,
                        zIndex: 2,
                    }}>
                        <tr>
                            <th>

                                <Stack padding={1} direction={'row'} alignItems={'center'} justifyContent={'flex-start'} spacing={1}>
                                    <Typography>Project</Typography>
                                    <IdeaSelect ideas={ratedProjects} onSelect={({ _id }) => {
                                        const index = ratedProjects.findIndex(proj => proj._id === _id);
                                        if (index >= 0) {
                                            ratedProjectElementRefs[index].current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                            setHighlightedIdea(_id);
                                        }
                                    }} />
                                </Stack>
                            </th>
                            {/* right after the team: it's the answer judges look for, so it mustn't scroll out of view */}
                            <th>
                                <Typography sx={{ padding: 1, fontWeight: 700 }}>Total (rank)</Typography>
                                {/* totals add up category averages: say what the most is, or 24.0 means nothing (Hermione, Oliver) */}
                                <Typography variant="caption" color="text.secondary" component="div">
                                    {hackathon.participantScores
                                        ? `judges (of ${judgeMax}) · participants (of ${participantMax})`
                                        : `judges, out of ${judgeMax}`}
                                </Typography>
                            </th>
                            {summary.categories.map(cat => (
                                <th
                                    key={cat}
                                >
                                    <Stack direction="row" alignItems="center" justifyContent="center" sx={{
                                        padding: 1,
                                        height: '100%',
                                        width: '100%',
                                    }}>
                                        <Typography>
                                            {cat}</Typography>

                                        <CategorySortMenu selected={sortCategory === cat ? sortType : undefined} onSelect={(type: string) => {
                                            setSortType(type);
                                            setSortCategory(cat);
                                        }} />
                                    </Stack>
                                    {/* the cells are colour-coded; say in words what each number is */}
                                    <Typography variant="caption" color="text.secondary" component="div">
                                        {hackathon.participantScores ? 'judges avg · participants avg' : "judges' average"}
                                    </Typography>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody style={{
                        maxHeight: 'calc(100vh - '
                    }}>
                        {
                            ratedProjects.map(({ categories, ...proj }, index) => {
                                const style = {
                                    backgroundColor: highlightedIdea === proj._id ? 'rgba(250, 204, 21, 0.14)' : 'transparent',
                                }
                                return (
                                    <React.Fragment key={proj._id}>
                                        <tr ref={ratedProjectElementRefs[index]} style={style}>
                                            <td style={{
                                                borderTop: '1px solid var(--border-color)',
                                                verticalAlign: 'top',
                                            }}>
                                                <Stack direction="column" spacing={1} sx={{ maxWidth: 320, minWidth: 160 }}>
                                                    <Typography variant={'h6'} sx={{ overflowWrap: 'anywhere' }}>{proj.title}</Typography>
                                                    {!isDue(proj._id) && <Chip size="small" variant="outlined" label="Hasn't presented yet" sx={{ alignSelf: 'flex-start' }} />}
                                                    {proj.members.length === 0 && <Chip size="small" color="warning" variant="outlined" label="No members yet" sx={{ alignSelf: 'flex-start' }} />}

                                                    {
                                                        !hackathon.blindJudgeScores && (
                                                            <FormControlLabel control={<Switch checked={showJudgeVotes.includes(proj._id)} onChange={(e) => {
                                                                if (e.currentTarget.checked) {
                                                                    setShowJudgeVotes([...showJudgeVotes, proj._id]);
                                                                } else {
                                                                    setShowJudgeVotes(showJudgeVotes.filter(id => id !== proj._id));
                                                                }
                                                            }} />} label="Show Judge Votes" />
                                                        )
                                                    }
                                                </Stack>

                                            </td>
                                            <td style={{ borderTop: '1px solid var(--border-color)' }}>
                                                <Stack direction="column" alignItems="center" spacing={0.5}>
                                                    <Stack direction="row" alignItems="center" justifyContent="center">
                                                        <Box sx={{ padding: 1, minWidth: 52, backgroundColor: judgeScoreBg, borderRadius: hackathon.participantScores ? '6px 0 0 6px' : '6px', fontWeight: 700, textAlign: 'center' }}
                                                            aria-label="Judges' total" title="Judges' total">
                                                            {judgeTotal({ ...proj, categories }) ? judgeTotal({ ...proj, categories }).toFixed(1) : '-'}
                                                        </Box>
                                                        {
                                                            hackathon.participantScores && (
                                                                <Box sx={{ padding: 1, minWidth: 52, backgroundColor: participantScoreBg, borderRadius: '0 6px 6px 0', fontWeight: 700, textAlign: 'center' }}
                                                                    aria-label="Participants' total" title="Participants' total">
                                                                    {participantTotal(categories) ? participantTotal(categories).toFixed(1) : '-'}
                                                                </Box>
                                                            )
                                                        }
                                                    </Stack>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {judgeRanks[proj._id] ? `#${judgeRanks[proj._id]} of ${ratedProjects.length}` : 'No judge scores yet'}
                                                    </Typography>
                                                </Stack>
                                            </td>
                                            {summary.categories.map(cat => {

                                                if (!categories[cat]) {
                                                    return <td key={cat} style={{
                                                        borderTop: '1px solid var(--border-color)',
                                                    }}></td>
                                                }
                                                return (
                                                    <td key={cat} style={{
                                                        borderTop: '1px solid var(--border-color)',
                                                    }}>
                                                        <Stack direction="column" spacing={2}>
                                                            <Stack direction="row" alignItems={'center'} justifyContent={'center'}>
                                                                <Box title="Judges' average" aria-label="Judges' average" sx={{
                                                                    padding: 1,
                                                                    width: 44,
                                                                    backgroundColor: judgeScoreBg,
                                                                    borderRadius: hackathon.participantScores ? '6px 0 0 6px' : '6px',
                                                                    fontWeight: 600,
                                                                    textAlign: 'center'
                                                                }}>
                                                                    {!categories[cat].judgeVoteAverage ? '-' : categories[cat].judgeVoteAverage.toFixed(1)}

                                                                </Box>
                                                                {
                                                                    // no participant number at all when participants don't score
                                                                    hackathon.participantScores && <Box title="Participants' average" aria-label="Participants' average" sx={{
                                                                        padding: 1,
                                                                        width: 44,
                                                                        backgroundColor: participantScoreBg,
                                                                        borderRadius: '0 6px 6px 0',
                                                                        fontWeight: 600,
                                                                        textAlign: 'center'
                                                                    }}>
                                                                        {categories[cat].participantVoteAverage === 0 ? '-' : categories[cat].participantVoteAverage.toFixed(1)}
                                                                    </Box>
                                                                }

                                                            </Stack>
                                                            {
                                                                showJudgeVotes.includes(proj._id) && (
                                                                    <Stack direction="row" justifyContent={'center'} spacing={0}>
                                                                        {categories[cat].judgeVotes.map((judgeVote, index) => {
                                                                            if (judgeVote.score === 0) {
                                                                                return <React.Fragment key={index}></React.Fragment>
                                                                            }
                                                                            return (
                                                                                <JudgeVoteBadge key={index} hackathon={hackathon} judgeId={judgeVote.judge ?? ''} score={judgeVote.score} />
                                                                            )
                                                                        })}
                                                                    </Stack>
                                                                )
                                                            }

                                                        </Stack>
                                                    </td>
                                                )
                                            })}
                                        </tr>
                                        <tr style={style}>
                                            <td colSpan={summary.categories.length + 2} style={{
                                                padding: "16px 0 0 0"
                                            }}>
                                                <Accordion disableGutters elevation={0} sx={{ backgroundColor: 'var(--surface-muted)', borderRadius: '10px', '&::before': { display: 'none' } }} expanded={expandedProjects.includes(proj._id)} onChange={() => handleExpand(proj._id)}>
                                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                        <Stack direction="row" spacing={1}>
                                                            <PeopleIcon />
                                                            <Typography>
                                                                {proj.members.length ? proj.members.map(m => m.username).join(', ') : 'No members yet'}
                                                            </Typography>
                                                        </Stack>
                                                    </AccordionSummary>
                                                    <Stack direction="column" spacing={1} padding={2}>
                                                        <Markdown>{proj.description}</Markdown>
                                                    </Stack>
                                                </Accordion>
                                            </td>
                                        </tr>
                                        <tr style={style}>
                                            <td colSpan={summary.categories.length + 2} style={{
                                                padding: "16px 0"
                                            }}>
                                                <Accordion disableGutters elevation={0} sx={{ backgroundColor: 'var(--surface-muted)', borderRadius: '10px', '&::before': { display: 'none' } }}
                                                    expanded={openVotes.includes(proj._id)} onChange={() => toggleVote(proj._id)}>
                                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                        {
                                                            scoredIdeas.has(proj._id)
                                                                ? <Stack direction="row" spacing={1} alignItems="center">
                                                                    <CheckCircleIcon color="success" fontSize="small" />
                                                                    <Typography sx={{ fontWeight: 600 }}>My vote: scored</Typography>
                                                                    <Typography color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                                                        {(scoredIdeas.get(proj._id) || []).map(s => s || '-').join(' · ')}
                                                                    </Typography>
                                                                </Stack>
                                                                : isDue(proj._id)
                                                                    ? <Typography sx={{ fontWeight: 600 }}>My vote: score this team</Typography>
                                                                    : <Typography color="text.secondary">My vote: not presented yet (you can score early)</Typography>
                                                        }
                                                    </AccordionSummary>
                                                    {/* the table can be wider than a phone; keep the score card on screen so every score is reachable */}
                                                    <Box sx={{ position: 'sticky', left: 0, maxWidth: 'calc(100vw - 72px)' }}>
                                                        <IdeaVoting hackathonId={id || hackathon.slug} hackathon={hackathon} idea={proj} onSubmit={() => {
                                                            aggregateVotes(hackathon._id);
                                                            // done with this team: fold it (its header now says "scored") and open the next one still to score
                                                            const next = dueProjects.find(p => p._id !== proj._id && !scoredIdeas.has(p._id));
                                                            setOpenVotes(open => [...open.filter(i => i !== proj._id), ...(next && !open.includes(next._id) ? [next._id] : [])]);
                                                        }} />
                                                    </Box>
                                                </Accordion>

                                            </td>
                                        </tr>
                                    </React.Fragment>
                                )
                            })
                        }
                    </tbody>
                </table>
                </Box>
            </Stack>
        </PageContainer >
    )
}